import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { RootState } from "../../store/store";
import incidentAlertsSlice from "../../store/slices/incidentAlerts.slice";
import { IncidentService } from "../../app/incidents/service";
import { WebIncidentService } from "../../app/operator/incidents/service";
import { ERole } from "../enums/user/ERole";
import { IUser } from "../interfaces/user/IUser";
import { IIncident } from "../interfaces/incident/IIncident";
import { getHomePath, hasRole } from "../functions/userRoles";
import { showNotificationAlert } from "../../utils/notification/notificationHelper";
import { useIncidentsListWebSocket } from "./useWebSocket";

// Payload the backend sends on /ws/incident
interface IIncidentListEvent {
  institutionId?: number;
  action?: string;
}

// Backend action values on the incident topics: "create" | "update" | "delete"
const NEW_INCIDENT_ACTION = "create";

const isNewIncidentEvent = (event: IIncidentListEvent): boolean =>
  event?.action?.toLowerCase() === NEW_INCIDENT_ACTION;

// Backend publishes every incident to /ws/incident (admin view) and each
// institution's incidents to /ws/incident/{institutionId} (operator view).
const getIncidentTopic = (userData?: IUser): string =>
  hasRole(userData, ERole.OPERATOR) && userData?.institutionId
    ? `/ws/incident/${userData.institutionId}`
    : "/ws/incident";

// Backend caps a page at 50 and sorts newest first. Without a count endpoint we
// count unviewed incidents among the latest 50, which covers active ones.
const UNVIEWED_COUNT_WINDOW = { page: 0, size: 50 };

const getIncidentListRequest = (userData?: IUser) => {
  if (hasRole(userData, ERole.ADMIN) || hasRole(userData, ERole.COMPANY)) {
    return IncidentService.getAll;
  }
  if (hasRole(userData, ERole.OPERATOR)) {
    return WebIncidentService.getAll;
  }
  return null;
};

const countUnviewed = (incidents: IIncident[] = []): number =>
  incidents.filter((incident) => incident.viewed === false).length;

/**
 * App-wide incident alerting: wherever the user is, a new incident plays the
 * alert, shows a toast and sends them to their home page (dashboard /
 * incident list). Every incident event also re-syncs the unviewed badge.
 */
export const useIncidentAlerts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const homePath = getHomePath(userData);

  // Kept in a ref so navigating does not re-subscribe to the socket
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const refreshUnviewedCount = useCallback(() => {
    const request = getIncidentListRequest(userData);
    request?.(UNVIEWED_COUNT_WINDOW)
      .then((response) =>
        dispatch(
          incidentAlertsSlice.actions.setUnviewedCount(
            countUnviewed(response?.data),
          ),
        ),
      )
      // Badge is non-critical; keep the last known count on failure
      .catch(() => undefined);
  }, [userData, dispatch]);

  const countRefreshRequest = useSelector(
    (state: RootState) => state.incidentAlerts.countRefreshRequest,
  );

  // Opening an incident marks it viewed on the backend, so re-sync on
  // navigation and whenever a page asks for it (e.g. after deleting)
  useEffect(() => {
    refreshUnviewedCount();
  }, [pathname, countRefreshRequest, refreshUnviewedCount]);

  const handleIncidentEvent = useCallback(
    (event: IIncidentListEvent) => {
      // Any create/update/delete can change the count (e.g. a deleted incident)
      refreshUnviewedCount();
      if (!isNewIncidentEvent(event)) return;
      showNotificationAlert();
      enqueueSnackbar(t("incident.newIncidentAlert"), { variant: "error" });
      if (pathnameRef.current !== homePath) {
        navigate(homePath);
      }
    },
    [homePath, t, navigate, refreshUnviewedCount],
  );

  useIncidentsListWebSocket(handleIncidentEvent, getIncidentTopic(userData));
};
